import { FormEvent, useState } from "react";
import styled from "styled-components";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";

import { Image } from "components/atoms";
import { AuthForm } from "components/molecules";

import authFormBg from "assets/images/authFormBg.png";
import logo from "assets/images/logo.svg";
import { BackgroundImageSize } from "interfaces/images";
import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_REGULAR } from "styles/typography";

const NavLinkContainer = styled.div`
	display: flex;
	justify-content: center;
	padding: 16px 0 0;
	width: 100%;
`;

const NavLink = styled(Link)`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	text-decoration: none;
	font-size: 14px;
	font-family: ${FONT_FAMILY_REGULAR};
	transition: color 0.15s ease;

	&:hover {
		color: ${MAIN_THEME.TEXT_PRIMARY};
	}
`;

const ErrorBox = styled.div`
	width: 100%;
	margin-top: 16px;
	padding: 12px 16px;
	border-radius: 12px;
	border: 1px solid rgba(255, 0, 110, 0.35);
	background: rgba(255, 0, 110, 0.08);
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-family: ${FONT_FAMILY_REGULAR};
	font-size: 14px;
	line-height: 1.4;
`;

const Container = styled.div`
	display: grid;
	grid-template-columns: 0.55fr 0.45fr;
	width: 100%;
	height: 100%;
	align-items: stretch;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const SideImage = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;

	@media (max-width: 768px) {
		display: none;
	}

	> div {
		height: 100%;
	}
`;

		const LogoWrap = styled.div`
			display: flex;
			justify-content: center;
			align-items: center;
			margin: 0 auto 32px;
			max-width: 312px;
			height: 70px;
			filter: brightness(0) invert(1);

			@media (max-width: 768px) {
				margin-bottom: 24px;
				max-width: 260px;
				height: 60px;
			}
		`;

const Form = styled.div`
	width: min(420px, 100%);
`;

const FormContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	width: 100%;
	padding: 48px 32px;

	@media (max-width: 768px) {
		padding: 40px 20px;
	}

	@media (max-width: 480px) {
		padding: 32px 16px;
	}
`;

	const SignIn = (): JSX.Element => {
		const [login, setLogin] = useState("");
		const [password, setPassword] = useState("");
		const [isLoading, setIsLoading] = useState(false);
		const [errorMessage, setErrorMessage] = useState<string>("");

		const getAuthErrorMessage = (error: any): string => {
			const type = error?.code || error?.name || error?.__type;

			switch (type) {
				case "UserNotFoundException":
				case "NotAuthorizedException":
					return "Incorrect email or password.";
				case "UserNotConfirmedException":
					return "Please confirm your email before signing in.";
				case "PasswordResetRequiredException":
					return "Password reset required. Please reset your password.";
				default:
					return error?.message || "Something went wrong. Please try again.";
			}
		};


	const handleChangeLogin = (value: string | number) => {
			setErrorMessage("");
			setLogin(`${value}`);
	};

	const handleChangePassword = (value: string | number) => {
			setErrorMessage("");
			setPassword(`${value}`);
	};

		const handleSubmit = async (event: FormEvent) => {
			event.preventDefault();
			setErrorMessage("");

			try {
				setIsLoading(true);

				const user = await Auth.signIn(login, password);

			if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
				Auth.completeNewPassword(
					user, // the Cognito User Object
					password // the new password
					// OPTIONAL, the required attributes
				).catch((e) => {
					console.log(e);
				});
			}

			} catch (error: any) {
				console.log({ error });
				setErrorMessage(getAuthErrorMessage(error));
			} finally {
				setIsLoading(false);
			}
		};

	return (
		<>
				<Container>
					<SideImage>
						<Image
							src={authFormBg}
							isBackground
							backgroundSize={BackgroundImageSize.COVER}
						/>
					</SideImage>

				<FormContainer>
					<Form>
							<LogoWrap>
								<Image src={logo} fullWidth />
							</LogoWrap>

							<AuthForm
								login={login}
								password={password}
								changeLogin={handleChangeLogin}
								changePassword={handleChangePassword}
								submit={handleSubmit}
								submitTitle="Sign In"
								isLoading={isLoading}
							/>
							{errorMessage ? <ErrorBox role="alert">{errorMessage}</ErrorBox> : null}
							<NavLinkContainer>
								<NavLink to="forgot-password">Forgot password?</NavLink>
							</NavLinkContainer>
						</Form>
					</FormContainer>
				</Container>
			</>
		);
	};

export default SignIn;
